import { parse } from 'date-fns';

import type { SentimentV2Filters } from '@revelio/data-access';

import { type Filter, FilterId, FilterType } from '../types/filters';
import { getCustomRoleFilter } from './getCustomRoleFilter';
import { clampDates, getDateFromFilters } from './getDateFilter';
import { getV2CompanyFilters } from './getV2CompanyFilters';
import { getV2GeographyFilters } from './getV2GeographyFilters';
import { stripVersionFromTaxonomyId } from './taxonomyHelpers';

const SentimentDateGranularity = 'month' as const;

export const getSentimentV2Filters = (
  effectiveFilters: Filter[],
  options?: {
    isSnapshot?: boolean;
  }
): SentimentV2Filters => {
  const isSnapshot = options?.isSnapshot ?? false;
  const baseFilters = effectiveFilters.reduce<SentimentV2Filters>(
    (acc, filter): SentimentV2Filters => {
      return ((): SentimentV2Filters => {
        switch (filter.id) {
          case FilterId.OVERALL_RATING: {
            if (filter.type === FilterType.STATIC_LIST) {
              return {
                ...acc,
                overall: filter.selections.map((selection) =>
                  // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
                  parseInt(selection.id)
                ),
              };
            }
            return acc;
          }

          case FilterId.ROLE_TAXONOMY: {
            if (filter.type === FilterType.VALUE && filter.value) {
              const customRoleAcc = acc.custom_role || {};
              return {
                ...acc,
                custom_role: {
                  ...customRoleAcc,
                  taxonomyId: stripVersionFromTaxonomyId(filter.value),
                },
              };
            }
            return acc;
          }

          case FilterId.SENIORITY: {
            if (filter.type === FilterType.LIST) {
              return {
                ...acc,
                seniority: filter.selections.map((selection) =>
                  // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
                  parseInt(selection.id)
                ),
              };
            }
            return acc;
          }

          case FilterId.ROLE:
            return {
              ...acc,
              ...getCustomRoleFilter(filter, effectiveFilters),
            };

          case FilterId.GEOGRAPHY:
            return { ...acc, ...getV2GeographyFilters(filter) };

          case FilterId.COMPANY:
            return { ...acc, ...getV2CompanyFilters(filter) };

          case FilterId.DATE_RANGE:
          case FilterId.SNAPSHOT_DATE:
          case FilterId.SKILL:
          case FilterId.GENDER:
          case FilterId.ETHNICITY:
          case FilterId.EDUCATION:
          case FilterId.KEYWORDS:
          case FilterId.TITLE:
          case FilterId.DATA_ADJUSTMENTS:
          case FilterId.POSTINGS_SOURCE:
          case FilterId.CONTINGENT_WORKERS:
            return acc;
          default:
            return acc;
        }
      })();
    },
    {}
  );

  const dateFilter = getDateFromFilters({
    filters: effectiveFilters,
    granularity: SentimentDateGranularity,
    isSnapshot,
  });

  if (isSnapshot) {
    return {
      ...baseFilters,
      ...(dateFilter && {
        start_date: dateFilter.start_date,
        end_date: dateFilter.end_date,
      }),
    };
  }

  return {
    ...baseFilters,
    ...clampDates({
      start_date: dateFilter?.start_date,
      end_date: dateFilter?.end_date,
      minStartDate: minSentimentStartDate,
      dateGranularity: SentimentDateGranularity,
    }),
  };
};

const minSentimentStartDate = parse('2020-01', 'yyyy-MM', new Date());
